import { Fragment, useState } from 'react'
import { UIEventFieldItem } from '../store/schemas'
import HoverableCellWrapper from '@/components/hoverable-cell-wrapper/HoverableCellWrapper'
import ReferencedSummarySentence from './ReferencedSummarySentence'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'

interface ReferencedSummaryCellProps {
  items: UIEventFieldItem[]
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
}

/**
 * Referenced Summary Cell
 *
 * Table cell component for displaying a list of summary sentences for a given event, handling paragraph breaks.
 * Wraps content with HoverableCellWrapper to support cell-level hover to highlight the first sentence in the cell.
 */
export default function ReferencedSummaryCell(props: ReferencedSummaryCellProps) {
  const { items, cellPosition, scrollToCell } = props
  const [cellHovered, setCellHovered] = useState(false)

  if (items.length === 0) {
    return <div className="px-4 py-2 text-sm text-brand-neutral-500 italic">None provided</div>
  }

  return (
    <HoverableCellWrapper onCellHover={setCellHovered}>
      <div className="px-4 py-2 text-sm">
        {items.map((item, index) => {
          // Remove newline before passing into ReferencedSummarySentence, paragraph breaks are handled below
          const textEndsWithNewline = item.text.endsWith('\n')
          const textWithoutNewline = textEndsWithNewline ? item.text.slice(0, -1) : item.text

          return (
            <Fragment key={`${item.reference_id}-${index}`}>
              <ReferencedSummarySentence
                referenceId={item.reference_id}
                text={textWithoutNewline}
                cellPosition={cellPosition}
                scrollToCell={scrollToCell}
                isFirst={index === 0}
                isCellHovered={cellHovered}
              />

              {/* Render a paragraph break below the sentence that will be captured by the cell hover */}
              {textEndsWithNewline && <span aria-hidden className="block h-4 pointer-events-none" />}
            </Fragment>
          )
        })}
      </div>
    </HoverableCellWrapper>
  )
}
